import { LinkedinCards } from "domain/entities/linkedincards";
import linkedInCardsRepository from "infra/repositories/linkedincards-repository";
import { useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import { LinkedinCardsInputFilters } from "domain/models/linkedincards-input-filters";
import { Logger } from "infra/logger/logger";
import { AxiosError } from "axios";

export const useLinkedInCardsByFilter = (filter: LinkedinCardsInputFilters) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["linkedincards", filter],
    queryFn: async () => {
        try {
            const result = await linkedInCardsRepository.getAll(filter);
            return result;
        } catch (error: any) {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
            );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    refetchInterval: Number(process.env.REACT_APP_QUERY_REFETCH_INTERVAL) || 100000,
    staleTime: 5000,
});
};

export const useLinkedinCardsById = (id?: string) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["linkedincards", id],
        queryFn: async () => {
            try {
                if (!id) throw new Error("ID inválido");
                const result = await linkedInCardsRepository.getById(id);
                return result;
            } catch (error: any) {
                if (error instanceof AxiosError) {
                logger.error(error.response?.data.metadata.errors[0],
                error.response?.data?.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
});
};

export const useCreateLinkedinCardsMutation = () => {
    const logger = Logger.getInstance();
    const queryLinkedinCards = useQueryClient();

    return useMutation({
        mutationFn: async (card: LinkedinCards) => {
            const result = await linkedInCardsRepository.create(card);
            return result;
        },
        onSuccess: (card) => {
            queryLinkedinCards.invalidateQueries({ queryKey: ["linkedincards", {}] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    //Cria um objeto de erro com o array de erros como propriedade
                    const customError = new Error("Erro ao criar cartão do LinkedIn");
                    (customError as any).errors = errors; // Adiciona o array de erros ao objeto de erro
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
                }
        }
    });
};

export const useDeleteLinkedinCardsMutation = () => {
    const logger = Logger.getInstance();
    const queryLinkedinCards = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!id) throw new Error("ID inválido para exclusão");
            const result = await linkedInCardsRepository.delete(id);
            return result;
        },
        onSuccess: (result, variables) => {
            queryLinkedinCards.invalidateQueries({
                queryKey: [
                "linkedincards",
            ] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
            );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    });
};

export const useLinkedInCardsRanking = (groupBy: string, page: number, limit: number, filters?: any) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["linkedincards", "ranking", groupBy, page, limit, filters],
    queryFn: async () => {
      try {
        const result = await linkedInCardsRepository.getRanking(groupBy, page, limit, filters);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: 3,
    staleTime: 5000,
  });
};

export const useLinkedInCardsDashboardStats = (filters?: any) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["linkedincards", "dashboard-stats", filters],
    queryFn: async () => {
      try {
        const result = await linkedInCardsRepository.getDashboardStats(filters);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: 3,
    staleTime: 10000,
  });
};
export const useLinkedInCardsFilterOptions = () => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["linkedincards", "filter-options"],
    queryFn: async () => {
      try {
        const result = await linkedInCardsRepository.getFilterOptions();
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: 3,
    staleTime: 60000, // as opções de filtro mudam pouco, podemos definir staleTime maior (1 minuto)
  });
};
